/* eslint-disable no-nested-ternary */
import { HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import { get, noop } from 'lodash-es';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import useAideEntry from 'hooks/business/useAideEntry';
import { isAeProduct, isQuickChat } from 'productLine';
import EntryItem from './EntryItem';
import Carousel from '../components/Carousel';
import { route as basisRoute, phraseRoute, timeoutReplyRoute, welcomeReplyRoute } from '../Basis';
import { route as qaRoute } from '../Qa';
import { route as orderRoute } from '../Order';
import { route as payRoute } from '../Pay';
import { route as logisticsRoute } from '../Logistics';
import { route as reviewRoute } from '../Review';
import { route as saveOrderRoute } from '../SaveOrder';
import { route as outStockRoute } from '../OutStock';
import raiseSvg from '../assets/raise.svg';
import { EntryOverview } from './EntryOverview';
import { useOrgValue } from 'hooks/business/system/useOrg';

export const Content = styled.div`
  flex: 1 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #d9e3fc;
  border-radius: 0 0 4px 4px;
  > div {
    min-width: 1160px;
    max-width: 1920px;
    width: 85%;
    padding: 50px 0;
  }
`;

export const EntryItemWrapper = styled.div<{ reverse: string }>`
  display: flex;
  justify-content: space-around;
  align-items: ${({ reverse }) => (reverse === 'true' ? 'flex-start' : 'flex-end')};
  position: relative;
  z-index: 1;
  > div {
    flex: 1 0 0;
    display: flex;
    justify-content: center;
  }
`;

export const Border = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 210px;
  margin: -46px 0 -36px 0;
  border: dashed 1px var(--ant-primary-color-hover);
  border-radius: 60px;
  > .anticon {
    position: absolute;
    color: var(--ant-primary-color-hover);
    &:nth-child(2n + 1) {
      top: -7.5px;
    }
    &:nth-child(2n) {
      bottom: -7.5px;
    }
    &:nth-child(3n + 1) {
      left: 25%;
    }
    &:nth-child(3n + 2) {
      left: 50%;
    }
    &:nth-child(3n) {
      left: 75%;
    }
  }
`;

const BorderLine = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  > .anticon {
    position: absolute;
    color: var(--ant-primary-color-hover);
  }
`;

export const Center = styled.div`
  border-radius: 16px;
  overflow: hidden;
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 18px;
    line-height: 56px;
    font-size: 26px;
    color: var(--theme-text-1);
    background: #cbdbfd;
    > img {
      width: 32px;
      height: 32px;
      margin-left: 8px;
    }
  }
  > section {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 18px;
    background: #ecf1fe;
    > * {
      width: 100%;
    }
  }
`;

export const CarsouselContent = styled.div`
  text-align: center;
`;

export interface EntryContentProps {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  regionDataMap: Partial<Record<General.UpperCaseRegion, Aide.Overview>>;
  overviewData?: Aide.Overview;
}

type ItemName =
  | 'AI智能接待'
  | '催下单'
  | '催付款'
  | '弃单挽回'
  | '评价管理'
  | '物流呵护'
  | '缺货提醒';

const commingItems: Partial<
  Record<General.PlatformUpperCaseTunnel, Partial<Record<ItemName, boolean>>>
> = {
  TOKOPEDIA: {
    催下单: true,
    催付款: true,
    弃单挽回: true,
    评价管理: true,
    物流呵护: true,
    缺货提醒: true,
  },
  ALIEXPRESS: {
    评价管理: true,
    弃单挽回: true,
    缺货提醒: true,
  },
  KWAI: {
    催下单: true,
    催付款: true,
    弃单挽回: true,
    评价管理: true,
    物流呵护: true,
    缺货提醒: true,
  },
};
// supportLazadaScenarios
export const isComming = (platform: General.CaselessPlatform, item: ItemName) => {
  if (platform === 'FACEBOOK') {
    return true;
  }
  return !!get(commingItems, `${toUpperCase(platform)}.${item}`);
};

function EntryContent(props: EntryContentProps) {
  const { platform, region, regionDataMap, overviewData } = props;

  const { t } = useTranslate();

  const { qaRegionList } = useFlagConfigValue();

  const { versionId } = useOrgValue() || {};

  const { hasData, totalTaskCount, enabledTaskCount, scenarioMap, carouselList } = useAideEntry(
    regionDataMap,
    overviewData,
  );

  const isMercado = platform === 'MERCADO';

  const itemProps = { platform, region };

  const aepluginProps: HTMLAttributes<HTMLDivElement> = isAeProduct
    ? {
        onClick: noop,
        style: { cursor: 'not-allowed' },
      }
    : {};

  const itemPropsMap = {
    phrase: {
      name: t('phrase.快捷回复'),
      description: <div>{t('aide.提升客服效率')}</div>,
      pageRoute: phraseRoute,
      scenario: scenarioMap.phrase,
    },
    welcome: {
      name: t('domain.aiReception.type.storeWelcomeMessage'),
      description: <div>{t('welcome_tips')}</div>,
      pageRoute: welcomeReplyRoute,
      scenario: scenarioMap.welcome,
    },
    timeout: {
      name: t('nav.autoReply'),
      description: <div>{t('timeout_auto_reply_tips')}</div>,
      pageRoute: timeoutReplyRoute,
      scenario: scenarioMap.timeout,
    },
    basic: {
      name: t('aide.基础接待'),
      description: (
        <>
          <div>{t('aide.提升客服效率')}</div>
          <span>{t('aide.高价值买家优先接待')}</span>
        </>
      ),
      pageRoute: basisRoute,
      scenario: scenarioMap.BASIC_RECEPTION,
    },
    qa: {
      name: t('domain.aiReception.type.aiReception'),
      online: !isComming(platform, 'AI智能接待') && qaRegionList.includes(toLowerCase(region)),
      description: (
        <>
          <span>{t('aide.提升咨询转化率')}</span>
          <div>{t('aide.节省人力成本')}</div>
        </>
      ),
      pageRoute: qaRoute,
      scenario: scenarioMap.AI_RECEPTION,
    },
    order: {
      name: t('marketing.催下单'),
      online: !isComming(platform, '催下单'),
      description: <div>{t('aide.提升店铺成单率')}</div>,
      pageRoute: orderRoute,
      scenario: scenarioMap.ORDER_REMINDER,
    },
    pay: {
      name: t('marketing.催付款'),
      online: !isComming(platform, '催付款'),
      description: (
        <>
          <div>{t('aide.提升付款率')}</div>
          <span>{t('aide.及时提醒买家付款')}</span>
        </>
      ),
      pageRoute: payRoute,
      scenario: scenarioMap.PAYMENT_REMINDER,
    },
    review: {
      name: t('review.评价管理'),
      online: !isComming(platform, '评价管理'),
      description: (
        <>
          <span>{t('aide.拉高店铺评分')}</span>
          <div>{t('aide.赚取平台流量')}</div>
        </>
      ),
      pageRoute: reviewRoute,
      scenario: scenarioMap.REVIEW_MANAGEMENT_V2,
      ...aepluginProps,
    },
    saveOrder: {
      name: t('aide.弃单挽回'),
      online: !isComming(platform, '弃单挽回'),
      description: (
        <>
          <span>{t('aide.逐级让利')}</span>
          <div>{t('aide.减少货品及运费损失')}</div>
        </>
      ),
      pageRoute: saveOrderRoute,
      scenario: scenarioMap.ORDER_ABANDON_REMINDER,
      ...aepluginProps,
    },
    logistics: {
      name: t('domain.aiReception.type.logisticsCare'),
      online: !isComming(platform, '物流呵护'),
      description: isMercado ? (
        <div>{t('aide.加快订单回款速度')}</div>
      ) : (
        <>
          <span>{t('aide.加快订单回款速度')}</span>
          <div>{t('aide.降低COD订单拒收率')}</div>
        </>
      ),
      pageRoute: logisticsRoute,
      scenario: scenarioMap.LOGISTICS_CARE,
    },
    outstock: {
      name: t('批量订单跟进'),
      online: !isComming(platform, '缺货提醒'),
      description: (
        <>
          <span>{t('及时告知买家订单问题')}</span>
          <div>{t('避免影响最终成交')}</div>
        </>
      ),
      pageRoute: outStockRoute,
      ...aepluginProps,
    },
  };

  type Key = keyof typeof itemPropsMap;

  const line1Keys: Key[] = isQuickChat
    ? isMercado
      ? ['phrase', 'welcome', 'timeout']
      : ['phrase', 'welcome', 'timeout', 'order']
    : isMercado
    ? ['basic', 'qa', 'outstock']
    : ['basic', 'qa', 'order', 'pay'];

  const line3Keys: Key[] = isQuickChat
    ? isMercado
      ? ['review', 'saveOrder', 'logistics']
      : ['review', 'saveOrder', 'logistics', 'pay']
    : isMercado
    ? ['review', 'logistics', 'saveOrder']
    : ['review', 'saveOrder', 'logistics', 'outstock'];

  const itemRender = (key: Key, reverse: boolean) => {
    return (
      <div>
        <EntryItem
          key={key}
          reverse={reverse}
          disabled={
            isQuickChat && versionId !== 7 && !['phrase', 'welcome', 'timeout'].includes(key)
          }
          {...itemProps}
          {...itemPropsMap[key]}
        />
      </div>
    );
  };

  const line1 = (
    <EntryItemWrapper reverse={String(false)}>
      {line1Keys.map((key) => itemRender(key, false))}
    </EntryItemWrapper>
  );

  const line3 = (
    <EntryItemWrapper reverse={String(true)}>
      {line3Keys.map((key) => itemRender(key, true))}
    </EntryItemWrapper>
  );

  const line2 = (
    <Border>
      <BorderLine className="top-[-7.5px]">
        {new Array(line1Keys.length - 1).fill('').map((_, index) => (
          <CaretRightOutlined style={{ left: `${(100 / line1Keys.length) * (index + 1)}%` }} />
        ))}
      </BorderLine>
      <BorderLine className="bottom-[7.5px]">
        {new Array(line3Keys.length - 1).fill('').map((_, index) => (
          <CaretLeftOutlined style={{ left: `${(100 / line1Keys.length) * (index + 1)}%` }} />
        ))}
      </BorderLine>
      <Center>
        <div style={isQuickChat ? { background: '#DCE1FD' } : undefined}>
          {t('aide.让每次接待都形成销售转化')}
          <img src={raiseSvg} alt="raise" />
        </div>
        <section style={isQuickChat ? { background: '#ECECFD' } : undefined}>
          <Carousel
            height="20px"
            contentList={carouselList.map((text) => {
              return <CarsouselContent key={text}>{text}</CarsouselContent>;
            })}
          />
        </section>
      </Center>
    </Border>
  );

  return (
    <div className="flex flex-col flex-grow flex-shrink-0 basis-0">
      <EntryOverview
        hasData={hasData}
        enabledTaskCount={enabledTaskCount}
        totalTaskCount={totalTaskCount}
      />
      <Content style={isQuickChat ? { background: '#DBD9FC' } : undefined}>
        <div>
          {line1}
          {line2}
          {line3}
        </div>
      </Content>
    </div>
  );
}

export default EntryContent;
